import type { DiasSemana } from './types';

/**
 * Sprint 74 — Cuándo está disponible algo: días de la semana y horas del día.
 *
 * ## Por qué existe
 *
 * Los carteles reales del negocio (`Requerimientos Negocio/Promos/`) dicen cosas como *"válido de
 * miércoles a viernes de 1PM a 5PM"*. El sistema sabía de días y **no sabía de horas**, así que la
 * *Promo Amigos* de $599 —1 kg de costilla más 4 micheladas— habría aplicado también a las nueve de
 * la noche, que es el horario de más venta. Dinero regalado por un campo que no existía.
 *
 * La alternativa era pedirle a la dueña que la prendiera a la una y la apagara a las cinco, todos
 * los días. Eso choca de frente con el veto de MASTER CHEF POS: *"no hagas que el día dependa de que
 * alguien recuerde un paso"*.
 *
 * ## Por qué es un módulo aparte
 *
 * Porque lo necesitan **dos** cosas: las promociones y los paquetes. Dos de los seis carteles
 * (*Promo Amigos* $599 y *Gratis medio pollo* $299) no son promociones sino **paquetes con precio de
 * promoción y horario**, y `Paquete` sólo tenía `habilitado`. Un solo concepto, una sola redacción y
 * una sola prueba para los dos.
 *
 * ## La medianoche
 *
 * Una ventana puede cruzar la medianoche —"viernes de 10 de la noche a 2 de la mañana"— y en este
 * ramo eso es lo normal, no la excepción: *el día operativo no termina a medianoche, termina cuando
 * el negocio cierra*. Cuando la hora final es menor o igual que la inicial, la ventana envuelve, y
 * entonces **el día de la semana se evalúa contra el día en que la ventana empezó**: a la una de la
 * mañana del sábado sigue corriendo la ventana del viernes. Tratar eso como "sábado" apagaría la
 * promoción justo a media venta.
 */

/** Minutos desde la medianoche. `null` en cualquiera de los dos extremos = todo el día. */
export interface Ventana {
  diasSemana: DiasSemana | null;
  horaInicio: number | null;
  horaFin: number | null;
}

/** `getDay()` devuelve 0 = domingo. */
const DIAS_POR_INDICE = [
  'domingo',
  'lunes',
  'martes',
  'miercoles',
  'jueves',
  'viernes',
  'sabado',
] as const;

export const MINUTOS_POR_DIA = 24 * 60;

/** Una ventana sin días ni horas: siempre disponible. Es el default de todo lo que existía antes. */
export const VENTANA_SIEMPRE: Ventana = { diasSemana: null, horaInicio: null, horaFin: null };

/** `"13:00"` → 780. Devuelve null para vacío o mal formado, que se interpreta como "todo el día". */
export function minutosDesdeHHMM(valor: string | null | undefined): number | null {
  if (!valor) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(valor.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** 780 → `"13:00"`, que es lo que espera un `<input type="time">`. */
export function hhmmDesdeMinutos(minutos: number | null | undefined): string {
  if (minutos === null || minutos === undefined) return '';
  const n = ((Math.trunc(minutos) % MINUTOS_POR_DIA) + MINUTOS_POR_DIA) % MINUTOS_POR_DIA;
  return `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
}

/**
 * 780 → `"1:00 p.m."`.
 *
 * En 12 horas a propósito: los carteles del negocio dicen "de 1PM a 5PM" y la dueña piensa en esos
 * términos. Un "13:00" en pantalla la obliga a traducir.
 */
export function horaLegible(minutos: number | null | undefined): string {
  if (minutos === null || minutos === undefined) return '';
  const n = ((Math.trunc(minutos) % MINUTOS_POR_DIA) + MINUTOS_POR_DIA) % MINUTOS_POR_DIA;
  const h24 = Math.floor(n / 60);
  const min = n % 60;
  const sufijo = h24 < 12 ? 'a.m.' : 'p.m.';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(min).padStart(2, '0')} ${sufijo}`;
}

/** La ventana tiene horas de verdad (las dos, y distintas). */
export function tieneHorario(v: Ventana): boolean {
  return (
    v.horaInicio !== null &&
    v.horaInicio !== undefined &&
    v.horaFin !== null &&
    v.horaFin !== undefined &&
    v.horaInicio !== v.horaFin
  );
}

/** Envuelve la medianoche: "de 10 p.m. a 2 a.m.". */
export function cruzaMedianoche(v: Ventana): boolean {
  return tieneHorario(v) && (v.horaFin as number) < (v.horaInicio as number);
}

function diaMarcado(dias: DiasSemana | null, indice: number): boolean {
  if (!dias) return true;
  return dias[DIAS_POR_INDICE[(indice + 7) % 7]];
}

/**
 * ¿Está dentro de la ventana en este instante?
 *
 * El intervalo de horas es **cerrado al inicio y abierto al final**: una promoción "de 1 a 5"
 * aplica a la 1:00 en punto y ya no a las 5:00. Es como lo lee cualquiera: a las cinco se acabó.
 */
export function dentroDeVentana(v: Ventana, ahora: Date): boolean {
  const minutoDelDia = ahora.getHours() * 60 + ahora.getMinutes();

  if (!tieneHorario(v)) {
    // Sin horario, la ventana es el día completo y el día es el de hoy.
    return diaMarcado(v.diasSemana, ahora.getDay());
  }

  const inicio = v.horaInicio as number;
  const fin = v.horaFin as number;

  if (!cruzaMedianoche(v)) {
    if (minutoDelDia < inicio || minutoDelDia >= fin) return false;
    return diaMarcado(v.diasSemana, ahora.getDay());
  }

  /*
    Ventana que envuelve la medianoche. La madrugada pertenece al día ANTERIOR: a la una de la mañana
    del sábado sigue corriendo la ventana del viernes. Evaluarla como "sábado" apagaría la promoción
    a media venta, que es justo el momento en que nadie puede ponerse a revisar configuraciones.
  */
  if (minutoDelDia >= inicio) return diaMarcado(v.diasSemana, ahora.getDay());
  if (minutoDelDia < fin) return diaMarcado(v.diasSemana, ahora.getDay() - 1);
  return false;
}

const NOMBRE_DIA: Array<[keyof DiasSemana, string]> = [
  ['lunes', 'lunes'],
  ['martes', 'martes'],
  ['miercoles', 'miércoles'],
  ['jueves', 'jueves'],
  ['viernes', 'viernes'],
  ['sabado', 'sábados'],
  ['domingo', 'domingos'],
];

/**
 * "los miércoles, jueves y viernes", o "" si aplican todos.
 *
 * Los días van en orden de semana laboral (lunes primero) porque así se anuncian, aunque `getDay()`
 * cuente desde el domingo.
 */
export function diasEnPalabras(dias: DiasSemana | null): string {
  if (!dias) return '';
  const activos = NOMBRE_DIA.filter(([k]) => dias[k]).map(([, n]) => n);
  if (activos.length === 0) return 'ningún día (así no va a aplicar nunca)';
  if (activos.length === 7) return '';
  if (activos.length === 1) return `los ${activos[0]}`;
  return `los ${activos.slice(0, -1).join(', ')} y ${activos[activos.length - 1]}`;
}

/** "de 1:00 p.m. a 5:00 p.m.", o "" si es todo el día. */
export function horarioEnPalabras(v: Ventana): string {
  if (!tieneHorario(v)) return '';
  return `de ${horaLegible(v.horaInicio)} a ${horaLegible(v.horaFin)}`;
}

/**
 * La ventana completa, para decírsela a un operador: "los miércoles, jueves y viernes de 1:00 p.m. a
 * 5:00 p.m.". Vacío si está disponible siempre — en ese caso no hay nada que advertir.
 */
export function ventanaEnPalabras(v: Ventana): string {
  const partes = [diasEnPalabras(v.diasSemana), horarioEnPalabras(v)].filter(Boolean);
  return partes.join(' ');
}
